import _ from "lodash";
import { Record, Map } from "immutable";
import { schema } from "normalizr";

import { NUser } from "@api/models";
import { AuthNormalisedEntities, IUser, User } from "@auth/models";
import { File, IFile, NFile, FilesNormalisedEntities } from "@files/models";
import { TranslatableMap } from "@localisation/models";
import { Label, ILabel, NLabel, MusicNormalisedEntities } from "@music/models";
import { HasCurrency, PaginationResult, RestModel, RestRecord } from "@orm/models";

interface ConfigMap {
    vars?: {
        [key: string]: any;
    };
}

interface ReportTypeConstraint {
    name: string;
    label: TranslatableMap;
    type: string;
}

interface TypeConfigMap {
    type?: string;
    constraints?: ReportTypeConstraint[];
}

export interface IReport extends RestModel {
    id?: number;
    description?: string;
    reason?: string;
    startDate?: Date;
    endDate?: Date;
    reportedDate?: Date;
    countryCode?: string;
    totalAmount?: number;
    currencyCode?: string;
    rowsProcessed?: number;
    processedAt?: Date;
    availableAt?: Date;
    config?: ConfigMap;
    organisationId?: number;
    reportTypeId?: number;
    statusName?: string;
    labelId?: number;
    generatedById?: number;
    createdAt?: Date;
    updatedAt?: Date;
    // Relations
    type?: IReportType | number;
    status?: IReportStatus | string;
    files?: Array<IFile | number>;
    label?: ILabel | number;
    generatedBy?: IUser | number;
}

export interface IReportType {
    id?: number;
    name?: string;
    label?: TranslatableMap;
    printableName?: string;
    isReportIn?: boolean;
    config?: TypeConfigMap;
}

export interface IReportStatus {
    name?: string;
    label?: TranslatableMap;
}

const ReportRecord = RestRecord({
    id: undefined,
    description: undefined,
    reason: undefined,
    startDate: undefined,
    endDate: undefined,
    reportedDate: undefined,
    countryCode: undefined,
    totalAmount: undefined,
    currencyCode: undefined,
    rowsProcessed: undefined,
    processedAt: undefined,
    availableAt: undefined,
    config: undefined,
    organisationId: undefined,
    reportTypeId: undefined,
    statusName: undefined,
    labelId: undefined,
    generatedById: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    type: undefined,
    status: undefined,
    files: undefined,
    label: undefined,
    generatedBy: undefined,
});

const ReportTypeRecord = Record({
    id: undefined,
    name: undefined,
    label: undefined,
    printableName: undefined,
    isReportIn: undefined,
    config: undefined,
});

const ReportStatusRecord = Record({
    name: undefined,
    label: undefined,
});

export class Report extends ReportRecord implements IReport, HasCurrency {
    public id: number;
    public description: string;
    public reason: string;
    public startDate: Date;
    public endDate: Date;
    public reportedDate: Date;
    public countryCode: string;
    public totalAmount: number;
    public currencyCode: string;
    public rowsProcessed: number;
    public processedAt: Date;
    public availableAt: Date;
    public config: ConfigMap;
    public organisationId: number;
    public reportTypeId: number;
    public statusName: string;
    public labelId: number;
    public generatedById: number;
    public createdAt: Date;
    public updatedAt: Date;
    // Relations
    public type: ReportType | number;
    public status: ReportStatus | string;
    public files: Array<File | number>;
    public label: Label | number;
    public generatedBy: User | number;

    constructor(params?: IReport) {
        // Create the relation records.
        if (params != null) {
            if ("type" in params && params.type != null &&
                typeof params.type === "object" &&
                !(params.type instanceof ReportType)
            ) {
                params.type = new ReportType(params.type);
            }
            if ("status" in params && params.status != null &&
                typeof params.status === "object" &&
                !(params.status instanceof ReportStatus)
            ) {
                params.status = new ReportStatus(params.status);
            }
            if ("files" in params && params.files != null &&
                params.files.every((file: IFile | number) => typeof file === "object")
            ) {
                params.files = _.map(params.files, (file: IFile) => {
                    return new File(file);
                });
            }
            if ("label" in params && params.label != null &&
                typeof params.label === "object" &&
                !(params.label instanceof Label)
            ) {
                params.label = new Label(params.label);
            }
            if ("generatedBy" in params && params.generatedBy != null &&
                typeof params.generatedBy === "object" &&
                !(params.generatedBy instanceof User)
            ) {
                params.generatedBy = new User(params.generatedBy);
            }
        }

        super(params);
    }

    public getFiles(): File[] {
        if (this.files != null &&
            this.files.every((file: File | number) => typeof file !== "number")
        ) {
            return this.files as File[];
        }
        return [];
    }

    public getType(): ReportType {
        if (this.type != null && typeof this.type !== "number") {
            return this.type as ReportType;
        }
        return null;
    }

    public getStatus(): ReportStatus {
        if (this.status != null && typeof this.status !== "string") {
            return this.status as ReportStatus;
        }
        return null;
    }

    public getLabel(): Label {
        if (this.label != null && typeof this.label !== "number") {
            return this.label as Label;
        }
        return null;
    }

    public getGeneratedBy(): User {
        if (this.generatedBy != null && typeof this.generatedBy !== "number") {
            return this.generatedBy as User;
        }
        return null;
    }
}

export class ReportType extends ReportTypeRecord implements IReportType {
    public id: number;
    public name: string;
    public label: TranslatableMap;
    public printableName: string;
    public isReportIn: boolean;
    public config: TypeConfigMap;

    public isCloudFunctionType(): boolean {
        return this.config?.type === "cloud-function";
    }
}

export class ReportStatus extends ReportStatusRecord implements IReportStatus {
    public name: string;
    public label: TranslatableMap;
}

export interface ReportsPaginationResult {
    list: number[];
    pagination: PaginationResult;
    lastLoaded: Date;
    requestId?: string;
}

export type ReportKeys = "statements" | "customReports";

export interface ReportsInternalState {
    entities: ReportsNormalisedEntities;
    statements: ReportsPaginationResult;
    customReports: ReportsPaginationResult;
}

export interface ReportsState {
    reports: ReportsInternalState;
}

export interface ReportsNormalisedEntities extends
    FilesNormalisedEntities, MusicNormalisedEntities, AuthNormalisedEntities
{
    reports?: Map<number, Report>;
    reportTypes?: Map<number, ReportType>;
    reportStatuses?: Map<string, ReportStatus>;
}

// Define the normalised version of the entities.
export const NReportStatus = new schema.Entity("reportStatuses", {}, {
    idAttribute: "name",
});

export const NReportType = new schema.Entity("reportTypes");

export const NReport = new schema.Entity("reports", {
    type: NReportType,
    status: NReportStatus,
    files: [ NFile ],
    label: NLabel,
    generatedBy: NUser,
});
